import { useEffect, useRef } from 'react';

/**
 * Custom hook for creating Matomo track-events. The event-parameters corresponds to their Matomo equivalents. (e.g., eventCategory = Event Category)
 * @param {string} eventCategory - The category of the tracked event.
 * @param {string} eventAction - The action associated with the event.
 * @param {string} eventName - The name of the event.
 * @param {number} eventValue - The value to track (e.g., an index).
 * @param {number} debounceTime - Debounce time in milliseconds.
 */
const useMatomoTrackEventOnChange = (
  eventCategory,
  eventAction,
  eventName,
  eventValue,
  debounceTime = 300
) => {
  if (
    typeof window === undefined ||
    !eventCategory ||
    !eventAction ||
    !eventName ||
    eventValue == null
  ) {
    return;
  }

  const timeoutRef = useRef(null);
  const eventValueUnchanged = useRef(true);

  useEffect(() => {
    if (eventValueUnchanged.current) {
      eventValueUnchanged.current = false;
      return;
    }

    clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      window?._paq?.push([
        'trackEvent',
        eventCategory,
        eventAction,
        eventName,
        eventValue,
      ]);
    }, debounceTime);

    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, [eventCategory, eventAction, eventValue]);
};

export default useMatomoTrackEventOnChange;
